import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { API } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import Seo, { SITE } from "@/components/Seo";
import { ResumeExperience, ResumeSkillsEdu } from "@/pages/resume/sections";

function GatedDownload({ r }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/resume/request", { email });
      setFileUrl(`${API}${res.data.downloadPath}`);
      toast.success("Done. Your download is ready.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (r.hasFile === false) {
    return (
      <p className="text-center text-[var(--brand-muted)] font-medium py-2" data-testid="resume-unavailable">
        The full PDF isn't available just yet. Please check back soon.
      </p>
    );
  }

  if (fileUrl) {
    return (
      <div className="text-center py-2" data-testid="resume-unlocked">
        <CheckCircle2 size={40} className="text-[var(--brand-primary)] mx-auto" />
        <h3 className="font-display text-2xl tracking-tight mt-3">You're all set.</h3>
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="resume-download-link"
          className="mt-5 inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-8 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-primary-dark)] shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]"
        >
          <Download size={18} /> Download resume (PDF)
        </a>
        <p className="mt-3 text-xs text-[color-mix(in_srgb,var(--brand-cream)_55%,transparent)]" data-testid="resume-link-expiry-note">This link is valid for 15 minutes.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="resume-form" className="grid sm:grid-cols-[1fr_auto] gap-3">
      <input
        data-testid="resume-email-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        required
        className="w-full bg-[var(--brand-cream)] border border-[#E7DFD0] rounded-full px-6 py-4 outline-none focus:ring-2 focus:ring-[var(--brand-primary)] font-medium"
      />
      <button
        data-testid="resume-submit-button"
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-primary-dark)] disabled:opacity-60 shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)] whitespace-nowrap"
      >
        {loading ? <><Loader2 size={18} className="animate-spin" /> Preparing…</> : <><Download size={18} /> {r.buttonLabel}</>}
      </button>
    </form>
  );
}

export default function Resume() {
  const { resume: r } = useContent();

  return (
    <div className="pt-28 pb-24">
      <Seo
        title="Resume | Kim Kornachuk | Product Marketing & GTM Leader"
        description="The full track record: LinkedIn, PRO-TEK Vaults, SparkPR, and Azeus Convene. Product marketing, demand generation, and GTM leadership across three continents."
        path="/resume"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: "Kim Kornachuk",
            alternateName: "Kimberly Dotado-Kornachuk",
            jobTitle: "Product Marketing & GTM Leader",
            url: SITE,
          },
        }}
      />
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-10 left-0 w-2/3 h-80 orange-wash opacity-60 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 sm:px-10 relative">
          <Link to="/" data-testid="resume-back-home" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[var(--brand-muted)] hover:text-[var(--brand-primary)] transition-colors mb-10">
            <ArrowLeft size={16} /> Back home
          </Link>
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] text-xs font-bold uppercase tracking-[0.1em] px-3 py-1 rounded-full mb-6">
              <FileText size={14} /> {r.pageTitle}
            </div>
            <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight t-heading">{r.fullName || r.headline}</h1>
            <p className="mt-3 font-display text-xl sm:text-2xl text-[var(--brand-primary)] tracking-tight">{r.title}</p>
            <p className="mt-6 text-lg text-[var(--brand-muted)] leading-relaxed font-medium max-w-3xl t-body">{r.summary}</p>
          </Reveal>

          {/* Highlights */}
          {r.highlights?.length > 0 && (
            <Reveal delay={0.1}>
              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {r.highlights.map((h, i) => (
                  <div key={h.label} className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-2xl p-5 shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)]">
                    <p className="font-display text-3xl sm:text-4xl text-[var(--brand-primary)] leading-none">{h.value}</p>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.08em] text-[var(--brand-heading)]">{h.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Experience */}
      <ResumeExperience experience={r.experience || []} />

      {/* Skills + Education/Certs/Awards */}
      <ResumeSkillsEdu r={r} />

      {/* Gated PDF download */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 mt-20">
        <Reveal>
          <div className="bg-[var(--brand-heading)] text-[var(--brand-cream)] rounded-[2rem] p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-72 h-72 orange-wash opacity-40 rounded-full blur-2xl" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{r.headline}</h2>
              <p className="mt-3 text-[color-mix(in_srgb,var(--brand-cream)_80%,transparent)] font-medium max-w-2xl">{r.description}</p>
              <div className="mt-7 max-w-xl">
                <GatedDownload r={r} />
              </div>
              <p className="mt-4 text-xs text-[color-mix(in_srgb,var(--brand-cream)_50%,transparent)]">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
